import type { SVGProps } from 'react';
import {
  Check,
  EditPencil,
  Filter,
  HalfMoon,
  HelpCircle,
  NavArrowDown,
  Plus,
  Refresh,
  SunLight,
  Trash,
  User,
  WarningTriangle,
  Xmark,
} from 'iconoir-react';

/**
 * The app's semantic icon layer.
 *
 * Every icon the app renders comes from here, named for the ACTION it
 * signifies (`DeleteIcon`, `NewIcon`, `ThemeDarkIcon`) rather than for the
 * glyph it happens to draw. Two reasons:
 *
 *   1. **One library, one place to swap it.** The concrete icon set
 *      (Iconoir) is imported only in this file. Switching libraries, or
 *      re-mapping which glyph an action uses, is a change here and nowhere
 *      else — no call site imports an icon package directly. (This file is
 *      the result of exactly that consolidation: the app previously imported
 *      a glyph library at ~14 call sites.)
 *   2. **Call sites read by meaning.** `<DeleteIcon />` says what the control
 *      does; `<Trash />` says what it looks like. The action name survives a
 *      glyph change; the glyph name doesn't.
 *
 * Each export is a thin wrapper that forwards all SVG props
 * (`className`, `aria-hidden`, `aria-label`, …) straight to the chosen glyph.
 * Sizing and colour stay the caller's job, exactly as before: callers pass
 * Tailwind `size-*` for size and the glyph inherits `currentColor` for colour,
 * so light/dark "just works" through the text token of wherever it sits.
 *
 * Accessibility is the call site's responsibility, not the layer's:
 *
 *   - **Decorative** icons (an icon beside a text label, or inside a button
 *     that already has an `aria-label`) pass `aria-hidden="true"` so screen
 *     readers don't double-announce. This is the common case.
 *   - **Icon-only** controls carry their accessible name on the interactive
 *     element (`aria-label` on the `<button>`), not on the glyph.
 *
 * Imports are per-icon (never `import * from 'iconoir-react'`) so the bundle
 * only carries the glyphs the app uses.
 *
 * The browseable inventory is Storybook `Design / Primitives / Icons`; the
 * design contract is `apps/client/design/components/icons.md`.
 */

export type IconProps = SVGProps<SVGSVGElement>;

/** New / create — the "+ New record" CTA. */
export function NewIcon(props: IconProps) {
  return <Plus {...props} />;
}

/** Delete (destructive) — record / passkey removal. */
export function DeleteIcon(props: IconProps) {
  return <Trash {...props} />;
}

/** Account / profile — the user-silhouette account control. */
export function AccountIcon(props: IconProps) {
  return <User {...props} />;
}

/** Filter — the status-filter funnel. */
export function FilterIcon(props: IconProps) {
  return <Filter {...props} />;
}

/** Theme toggle — light (the resting glyph in dark mode: "switch to light"). */
export function ThemeLightIcon(props: IconProps) {
  return <SunLight {...props} />;
}

/** Theme toggle — dark (the resting glyph in light mode: "switch to dark"). */
export function ThemeDarkIcon(props: IconProps) {
  return <HalfMoon {...props} />;
}

/** Edit / rename. */
export function EditIcon(props: IconProps) {
  return <EditPencil {...props} />;
}

/** Re-analyze — refresh a stale analysis. */
export function ReanalyzeIcon(props: IconProps) {
  return <Refresh {...props} />;
}

/** Close — dialog / overlay dismiss. */
export function CloseIcon(props: IconProps) {
  return <Xmark {...props} />;
}

/** Warning — the leading glyph on an alert/notice. */
export function WarningIcon(props: IconProps) {
  return <WarningTriangle {...props} />;
}

/**
 * Help / hint — the `?` affordance on instruction tooltips.
 *
 * Iconoir has no question-mark-in-circle export, so this uses Iconoir's own
 * `HelpCircle` (a lifebuoy-style help glyph) — the nearest in-set match.
 */
export function HelpIcon(props: IconProps) {
  return <HelpCircle {...props} />;
}

/** Dropdown affordance — the chevron on a menu trigger. */
export function DropdownIcon(props: IconProps) {
  return <NavArrowDown {...props} />;
}

/** Selected — the check on the active menu item. */
export function SelectedIcon(props: IconProps) {
  return <Check {...props} />;
}
